"use client";

import * as React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, Wrench, TriangleAlert as AlertTriangle, Flame, Droplets, Volume2, Clock, ShieldCheck, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "item-1",
  question: "Why isn't my water heater producing hot water?",
  icon: Flame,
  answer: "A complete loss of hot water usually stems from an extinguished pilot light, a faulty thermocouple, or a tripped circuit breaker on electric models. On electric units, a failed upper heating element or thermostat will also stop heat generation. We test electrical components and gas controls to pinpoint the breakdown quickly."
}, {
  id: "item-2",
  question: "Why is my water heater leaking?",
  icon: Droplets,
  answer: "Leaks from the top typically come from loose pipe fittings, failing dielectric nipples, or a worn temperature and pressure (T&P) relief valve. Leaks pooling around the base often indicate internal tank corrosion. When the inner tank fractures from sediment buildup or age, tank replacement is necessary to prevent severe water damage."
}, {
  id: "item-3",
  question: "Why does my water heater make popping or rumbling noises?",
  icon: Volume2,
  answer: "Popping, rumbling, or knocking sounds are caused by mineral and sediment accumulation at the bottom of the tank. As water gets trapped beneath the sediment layer and boils, steam bubbles burst through the debris. An annual tank flush dissolves and clears sediment, restoring efficient heat transfer and preventing premature bottom tank burn-through."
}, {
  id: "item-4",
  question: "Why does my hot water run out much faster than before?",
  icon: Clock,
  answer: "If showers go cold quickly, the lower heating element in an electric heater may have burned out, or a heavy layer of sediment may be insulating the water from the gas burner. In older units, a broken dip tube can also allow cold incoming water to mix directly with the hot water exiting the top of the tank."
}, {
  id: "item-5",
  question: "Should I repair or replace my water heater?",
  icon: ShieldCheck,
  answer: "As a standard guideline, units under 8-10 years old with minor component failures (such as valves, thermocouples, or heating elements) are cost-effective to repair. If the tank itself is leaking, past its warranty lifespan, or repair costs exceed 50% of the replacement price, installing a high-efficiency replacement is the recommended long-term solution."
}, {
  id: "item-6",
  question: "Do you service gas, electric, and tankless water heaters?",
  icon: Wrench,
  answer: "Yes. Our certified technicians carry parts and diagnostic equipment for standard atmospheric gas tanks, power-vent systems, dual-element electric tanks, and high-efficiency on-demand tankless water heaters across all major commercial and residential manufacturers."
}, {
  id: "item-7",
  question: "How can I tell if my pressure relief valve is failing?",
  icon: AlertTriangle,
  answer: "The Temperature and Pressure (T&P) Relief Valve safely releases excess heat and pressure. Warning signs of failure include constant dripping from the discharge pipe, mineral crusting around the valve head, or valve failure during manual testing. A malfunctioning T&P valve poses serious safety risks and should be replaced immediately."
}];
export default function WaterHeaterFaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="waterheaterfaq" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground transition-colors overflow-hidden">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold tracking-wider uppercase border-accent text-accent-foreground bg-accent/10">
            <HelpCircle className="size-3.5 mr-1 text-accent" />
            Diagnostic FAQ
          </Badge>

          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Common Water Heater Questions & Solutions
          </h2>

          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Get clear, diagnostic answers to common water heater failures, leaks, and noise issues in Jonesboro homes before scheduling service.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto mb-14">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqList.map((faq, i) => {
            const Icon = faq.icon;
            return <div key={faq.id} data-index={i}>
                  <AccordionItem value={faq.id} className="border border-border rounded-lg bg-card px-4 md:px-6 shadow-xs transition-all duration-200 data-[state=open]:border-accent/60 data-[state=open]:shadow-sm">
                    <AccordionTrigger className="text-base sm:text-lg font-semibold py-4 md:py-5 text-foreground hover:no-underline group">
                      <div className="flex items-center gap-3 text-left">
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                          <Icon className="size-4" />
                        </span>
                        <span className="group-hover:text-primary transition-colors">
                          {faq.question}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-5 pl-11">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>;
          })}
          </Accordion>
        </div>

        {/* Bottom CTA Card */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="max-w-3xl mx-auto rounded-xl border border-border bg-card p-6 md:p-8 text-center shadow-md relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-accent" />
          
          <div className="flex flex-col items-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
              <PhoneCall className="size-6" />
            </span>

            <h3 className="text-xl md:text-2xl font-bold text-foreground font-default mb-2">
              Need Immediate Water Heater Diagnostics or Repair?
            </h3>

            <p className="text-muted-foreground text-sm md:text-base max-w-lg mb-6">
              Our Jonesboro emergency plumbers provide rapid response, same-day repairs, and new unit installations across Clayton County.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
                <a href="tel:4708479076">
                  <PhoneCall className="size-4 mr-2" />
                  Call (470) 847-9076
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border hover:bg-secondary font-medium">
                <Link to="/water-heater-repair/">
                  Request Service
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}